/**
 * Agency model — wire types for /admin/agencies*, /admin/agency-settings,
 * /admin/agency-payouts and /admin/agency-portal. Money is paise as a decimal
 * string (the API's BigInt JSON form); rates are basis points (1000 = 10%).
 */

export type AgencyStatus = 'active' | 'suspended';
export type CommissionBase = 'net_of_tax' | 'gross';
export type CommissionStatus = 'accrued' | 'reversed' | 'skipped';
export type PayoutStatus = 'pending_approval' | 'approved' | 'processing' | 'settled' | 'reversed' | 'rejected';

export interface CurrencyAmount {
  currency: string;
  count: number;
  amountPaise: string;
}

export interface CommissionSummary {
  accrued: CurrencyAmount[];
  reversed: CurrencyAmount[];
  skippedCount: number;
}

export interface AgencyBalance {
  balancePaise: string;
  heldPaise: string;
  payablePaise: string;
  minPayoutPaise: string;
  tdsBps: number;
  openPayoutId: string | null;
}

export interface PayoutDetails {
  payoutMethod: 'bank' | 'upi' | null;
  accountHolderName: string | null;
  bankName: string | null;
  accountNumber: string | null;
  ifsc: string | null;
  upiId: string | null;
  pan: string | null;
}

export interface AgencyBase {
  id: string;
  code: string;
  name: string;
  contactName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  country: string;
  status: AgencyStatus;
  /** Override; null = the global default. */
  commissionBps: number | null;
  commissionWindowDays: number | null;
  commissionEnabled: boolean;
  /**
   * Dude-team switches for the agency portal. Off = view only. Optional so an
   * older API (no switches yet) reads as "off".
   */
  controls?: AgencyControls;
  notes: string | null;
  createdAt: string;
}

export interface AgencyControls {
  /** Approve / reject its own listener applications. */
  listeners: boolean;
  /** Block / unblock its own users. */
  users: boolean;
}

export interface AgencyListRow extends AgencyBase {
  effectiveCommissionBps: number;
  login: { email: string; disabled: boolean } | null;
  users: number;
  commission: CommissionSummary;
}

export interface AgencyDetail extends AgencyBase {
  payoutDetails: PayoutDetails;
  effectiveCommissionBps: number;
  effectiveWindowDays: number;
  login: { email: string; disabled: boolean } | null;
  users: number;
  payingUsers: number;
  commission: CommissionSummary;
  balance: AgencyBalance;
  rateChanges: Array<{ oldBps: number | null; newBps: number | null; changedBy: string | null; createdAt: string }>;
}

export interface AgencySettings {
  defaultCommissionBps: number;
  commissionBase: CommissionBase;
  commissionWindowDays: number;
  holdDays: number;
  minPayoutPaise: string;
  tdsBps: number;
  portalPayoutRequests: boolean;
  attachWindowDays: number;
}

export interface AgencyUserRow {
  userId: string;
  handle: string;
  displayName: string | null;
  joinedAt: string;
  deleted: boolean;
  source: 'manual' | 'link' | 'admin';
  attributedAt: string;
  recharges: CurrencyAmount[];
}

export interface AgencyCommissionRef {
  status: CommissionStatus;
  amountPaise: string;
  rateBps: number;
  skipReason: string | null;
  payableAt: string;
  payoutId: string | null;
}

export interface AgencyRechargeRow {
  orderId: string;
  userId: string;
  handle: string;
  packageName: string | null;
  coins: number | null;
  bonusCoins: number;
  amountPaise: string;
  currency: string;
  gateway: string;
  status: 'captured' | 'refunded';
  capturedAt: string | null;
  commission: AgencyCommissionRef | null;
}

export interface AgencyCommissionRow {
  id: string;
  paymentOrderId: string;
  userId: string;
  handle: string | null;
  grossPaise: string;
  basePaise: string;
  baseMode: CommissionBase;
  rateBps: number;
  amountPaise: string;
  currency: string;
  status: CommissionStatus;
  skipReason: string | null;
  payableAt: string;
  payoutId: string | null;
  reversedAt: string | null;
  capturedAt: string;
}

export interface AgencyPayoutRow {
  id: string;
  agencyId: string;
  agency?: { id: string; code: string; name: string };
  amountPaise: string;
  tdsBps: number;
  tdsPaise: string;
  netPayoutPaise: string;
  currency: string;
  status: PayoutStatus;
  requestedBy: 'agency' | 'admin';
  payoutMethod: string | null;
  accountHolderName: string | null;
  bankName: string | null;
  accountNumber: string | null;
  ifsc: string | null;
  upiId: string | null;
  approvedAt: string | null;
  paymentReference: string | null;
  settledAt: string | null;
  reversalReason: string | null;
  createdAt: string;
}

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

export interface PortalSummary {
  agency: AgencyBase & { payoutDetails: PayoutDetails };
  commissionBps: number;
  commissionBase: CommissionBase;
  holdDays: number;
  canRequestPayout: boolean;
  users: number;
  payingUsers: number;
  recharges: { allTime: CurrencyAmount[]; thisMonth: CurrencyAmount[] };
  commission: CommissionSummary;
  balance: AgencyBalance;
}

/** 1000 → "10%", 750 → "7.5%". */
export function formatBps(bps: number | null | undefined): string {
  if (bps === null || bps === undefined) return '—';
  return `${(bps / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 })}%`;
}

/** Percent typed by an operator → basis points (7.5 → 750). */
export function percentToBps(pct: number): number {
  return Math.round(pct * 100);
}

export const SKIP_REASON_LABEL: Record<string, string> = {
  test_order: 'Test top-up',
  refunded_before_accrual: 'Refunded',
  agency_suspended: 'Agency suspended',
  commission_disabled: 'Commission off',
  outside_window: 'Outside commission window',
  zero_rate: 'Rate is 0%',
  zero_amount: 'Too small',
  unsupported_currency: 'Other currency',
};
